import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import { StyleSheet, css } from 'aphrodite'
import moment from 'moment-timezone'
import { Helper } from '../helpers/Helper'
import { WhatsappUtils } from '../../screens/admin/cdp/WhatsappUtils'

function safeStr(value) {
  if (value == null) return ''
  return String(value)
}

function upper(value) {
  return safeStr(value).trim().toUpperCase()
}

function parseBodyRichText(bodyRichText) {
  if (!bodyRichText) return ''
  return WhatsappUtils.waRichTextToWhatsAppBodyText(bodyRichText)
}

function lower(value) {
  return safeStr(value).trim().toLowerCase()
}

function getButtonLabel(type, text, fallback) {
  const t = upper(type)
  if (t === 'NONE') return ''
  return safeStr(text).trim() || fallback
}

function getListRows(session) {
  const rows = []
  for (let i = 1; i <= 10; i++) {
    const id = safeStr(session[`row${i}Id`]).trim()
    if (!id) break
    rows.push({
      id,
      title: safeStr(session[`row${i}Title`]).trim() || `Row ${i}`,
      description: safeStr(session[`row${i}Description`]).trim(),
    })
  }
  return rows
}

export class WhatsappSessionPreview extends Component {
  constructor(props) {
    super(props)
    this.state = {
      now: new Date(),
      isBodyExpanded: false,
      loadedHeaderImageUrl: '',
      isListDialogOpen: false,
      selectedListRowId: '',
    }
    this.timeIntervalId = null
  }

  resolvedSession(props = this.props) {
    const p = props
    const base = Object.prototype.hasOwnProperty.call(p, 'formData') ? { ...(p.formData || {}) } : { ...(p.session || {}) }
    const fields = p.context && p.context.formDefinition && p.context.formDefinition.steps
      && p.context.formDefinition.steps[0] && p.context.formDefinition.steps[0].fields
    if (!Array.isArray(fields)) return base
    const out = { ...base }
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i]
      if (!f || !f.linkName || !f.link) continue
      const v = out[f.linkName]
      if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) {
        out[f.linkName] = f.link
      }
    }
    const interactivityType = upper(out.interactivityType)
    if (interactivityType === 'LIST') {
      out.button1Type = 'LIST'
      out.button1Text = safeStr(out.interactivityLabel || out.button1Text).trim()
      out.button2Type = 'NONE'
      out.button3Type = 'NONE'
    }
    return out
  }

  componentDidMount() {
    if (this.props.messageBubbleMode) return
    this.timeIntervalId = setInterval(() => {
      this.setState({ now: new Date() })
    }, 60000)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.session === this.props.session && prevProps.formData === this.props.formData && prevProps.context === this.props.context) {
      return
    }
    const prevSession = this.resolvedSession(prevProps)
    const nextSession = this.resolvedSession(this.props)
    const prevMediaUrl = safeStr(prevSession.mediaLink).trim()
    const nextMediaUrl = safeStr(nextSession.mediaLink).trim()
    this.setState({
      isBodyExpanded: false,
      loadedHeaderImageUrl: prevMediaUrl !== nextMediaUrl ? '' : this.state.loadedHeaderImageUrl,
      isListDialogOpen: false,
      selectedListRowId: '',
    })
  }

  componentWillUnmount() {
    if (this.timeIntervalId != null) {
      clearInterval(this.timeIntervalId)
      this.timeIntervalId = null
    }
  }

  formatBubbleTime = (date) => {
    const timezone = Helper.getLocalTimezone()
    return moment(date).tz(timezone).format('h:mm a')
  }

  isBodyLong = (text) => {
    const s = safeStr(text)
    return s.length > 220 || s.split('\n').length > 4
  }

  getCollapsedBodyText = (text) => {
    const s = safeStr(text).trim()
    const lines = s.split('\n')
    if (lines.length > 4) {
      return `${lines.slice(0, 4).join('\n').trimEnd()}...`
    }
    if (s.length <= 180) return s
    return `${s.slice(0, 177).trimEnd()}...`
  }

  renderBodyText = (rawText) => {
    const lines = safeStr(rawText).split('\n')
    return lines.map((line, i) => (
      <span key={`line_${i}`} className={css(styles.lineWrap)}>
        {this.renderLineWithListSupport(line)}
        {i < lines.length - 1 ? <br /> : null}
      </span>
    ))
  }

  renderLineWithListSupport = (line) => {
    const raw = safeStr(line)
    const trimmed = raw.trimStart()
    if (trimmed.startsWith('- ')) {
      const bulletText = trimmed.slice(2)
      return (
        <span className={css(styles.bulletLine)}>
          <span className={css(styles.bulletGlyph)}>•</span>
          <span className={css(styles.bulletText)}>{this.renderInlineFormatting(bulletText)}</span>
        </span>
      )
    }
    return this.renderInlineFormatting(raw)
  }

  renderInlineFormatting = (text) => {
    const nodes = []
    const input = safeStr(text)
    const tokenRegex = /(\*_[^]+?_\*|_\*[^]+?\*_|\*[^*]+\*|_[^_]+_|~[^~]+~|```[^`]+```)/g
    let lastIndex = 0
    let match
    let key = 0

    while ((match = tokenRegex.exec(input)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(<span key={`txt_${key++}`}>{input.slice(lastIndex, match.index)}</span>)
      }
      const token = match[0]
      if (token.startsWith('*_') && token.endsWith('_*')) {
        const content = token.slice(2, -2)
        nodes.push(<strong key={`fmt_${key++}`}><em>{content}</em></strong>)
      } else if (token.startsWith('_*') && token.endsWith('*_')) {
        const content = token.slice(2, -2)
        nodes.push(<em key={`fmt_${key++}`}><strong>{content}</strong></em>)
      } else if (token.startsWith('*') && token.endsWith('*')) {
        const content = token.length > 2 ? token.slice(1, -1) : token
        nodes.push(<strong key={`fmt_${key++}`}>{content}</strong>)
      } else if (token.startsWith('_') && token.endsWith('_')) {
        const content = token.length > 2 ? token.slice(1, -1) : token
        nodes.push(<em key={`fmt_${key++}`}>{content}</em>)
      } else if (token.startsWith('~') && token.endsWith('~')) {
        const content = token.length > 2 ? token.slice(1, -1) : token
        nodes.push(<span key={`fmt_${key++}`} className={css(styles.strike)}>{content}</span>)
      } else if (token.startsWith('```') && token.endsWith('```')) {
        nodes.push(<code key={`fmt_${key++}`} className={css(styles.code)}>{token.slice(3, -3)}</code>)
      } else {
        nodes.push(<span key={`fmt_${key++}`}>{token}</span>)
      }
      lastIndex = tokenRegex.lastIndex
    }

    if (lastIndex < input.length) {
      nodes.push(<span key={`txt_${key++}`}>{input.slice(lastIndex)}</span>)
    }
    return nodes
  }

  renderHeader = () => {
    const session = this.resolvedSession()
    const bubbleMode = !!this.props.messageBubbleMode
    const headerType = upper(session.headerType || 'NONE')
    if (headerType === 'NONE') return null
    if (headerType === 'TEXT') {
      const text = safeStr(session.headerText).trim()
      if (!text) return null
      return (
        <div className={css(styles.headerText, bubbleMode && styles.headerTextBubbleMode)}>
          {this.renderBodyText(text)}
        </div>
      )
    }
    const mediaUrl = safeStr(session.mediaLink || session.media).trim()
    if (!mediaUrl) return <div className={css(styles.missingMedia)}>{headerType}</div>
    if (headerType === 'IMAGE') {
      const imageLoaded = this.state.loadedHeaderImageUrl === mediaUrl
      return (
        <div className={css(
          styles.headerImageWrap,
          bubbleMode && styles.headerImageWrapBubbleMode,
          !imageLoaded && styles.headerImageWrapLoading,
          bubbleMode && !imageLoaded && styles.headerImageWrapLoadingBubbleMode
        )}>
          {!imageLoaded && <div className={css(styles.headerImagePlaceholder)} />}
          <img
            src={mediaUrl}
            alt='whatsapp-session-header'
            className={css(
              styles.headerImage,
              bubbleMode && styles.headerImageBubbleMode,
              imageLoaded ? styles.headerImageVisible : styles.headerImageHidden
            )}
            onLoad={() => {
              if (this.state.loadedHeaderImageUrl !== mediaUrl) {
                this.setState({ loadedHeaderImageUrl: mediaUrl })
              }
            }}
          />
        </div>
      )
    }
    if (headerType === 'VIDEO') {
      const video = (
        <video
          key={mediaUrl}
          className={css(styles.headerVideo, bubbleMode && styles.headerVideoBubbleMode)}
          controls
          controlsList='nodownload noplaybackrate'
          src={mediaUrl}
        />
      )
      if (bubbleMode) {
        return <div className={css(styles.headerVideoWrapBubbleMode)}>{video}</div>
      }
      return video
    }
    if (headerType === 'DOCUMENT') {
      const doc = this.buildDocumentMetaFromUrl(mediaUrl, safeStr(session.fileName).trim())
      return (
        <a
          href={mediaUrl}
          target='_blank'
          rel='noopener noreferrer'
          className={css(styles.documentCard, bubbleMode && styles.documentCardBubbleMode)}
        >
          <div className={css(styles.documentTop)}>
            <div className={css(styles.documentLeft)}>
              <div className={css(styles.documentFileTile)}>
                <span className={`material-icons-outlined ${css(styles.documentFileIcon)}`}>description</span>
                <div className={css(styles.documentFileType)}>{doc.extLabel}</div>
              </div>
            </div>
            <div className={css(styles.documentMeta)}>
              <div className={css(styles.documentName)} title={doc.fileName}>
                {doc.fileName}
              </div>
              <div className={css(styles.documentSub)}>Document</div>
            </div>
            <div className={css(styles.documentDownload)}>
              <span className={`material-icons-outlined ${css(styles.documentDownloadIcon)}`}>file_download</span>
            </div>
          </div>
        </a>
      )
    }
    return <div className={css(styles.missingMedia)}>{headerType}</div>
  }

  renderBubbleTimeLabel = () => {
    const label = safeStr(this.props.bubbleTimeLabel).trim()
    if (label) return label
    return this.formatBubbleTime(this.state.now)
  }

  renderBody = () => {
    const session = this.resolvedSession()
    const bodyText = safeStr(session.bodyText).trim() || parseBodyRichText(session.bodyRichText)
    if (!bodyText) return null
    const bubbleMode = !!this.props.messageBubbleMode
    const longBody = this.isBodyLong(bodyText)
    const collapsed = this.getCollapsedBodyText(bodyText)
    const showReadMore = !bubbleMode && longBody && !this.state.isBodyExpanded && collapsed !== bodyText.trim()
    const textToRender = showReadMore ? collapsed : bodyText
    return (
      <div className={css(styles.bodyText, bubbleMode && styles.bodyTextBubbleMode)}>
        {this.renderBodyText(textToRender)}
        {showReadMore && (
          <button
            type='button'
            className={css(styles.readMoreButton)}
            onClick={() => this.setState({ isBodyExpanded: true })}
          >
            Read more
          </button>
        )}
      </div>
    )
  }

  buildDocumentMetaFromUrl = (url, configuredName = '') => {
    const raw = safeStr(url)
    const safe = raw.split('#')[0].split('?')[0]
    const last = safe.split('/').filter(Boolean).pop() || ''
    const inferredName = last ? decodeURIComponent(last) : ''
    const fileNameRaw = configuredName || inferredName || 'Document.pdf'
    const fileName = fileNameRaw.length <= 60 ? fileNameRaw : `${fileNameRaw.slice(0, 57)}...`
    const ext = (fileNameRaw.includes('.')) ? fileNameRaw.split('.').pop().toLowerCase() : ''
    const extLabel = (ext && ext.length <= 5) ? ext.toUpperCase() : 'PDF'
    return { fileName, extLabel }
  }

  renderFooter = () => {
    const footerText = safeStr(this.resolvedSession().footerText).trim()
    if (!footerText) return null
    return <span className={css(styles.footerText)}>{footerText}</span>
  }

  renderButtons = () => {
    const session = this.resolvedSession()
    const b1Type = upper(session.button1Type || 'NONE')
    const b2Type = upper(session.button2Type || 'NONE')
    const b3Type = upper(session.button3Type || 'NONE')
    const labels = [
      getButtonLabel(b1Type, session.button1Text, 'Button 1'),
      b2Type === 'QUICK_REPLY' ? getButtonLabel(b2Type, session.button2Text, 'Button 2') : '',
      b3Type === 'QUICK_REPLY' ? getButtonLabel(b3Type, session.button3Text, 'Button 3') : '',
    ].filter(Boolean)

    if (labels.length === 0) return null
    const buttonRows = []
    if (labels[0]) buttonRows.push({ type: b1Type, text: labels[0], url: safeStr(session.button1Url).trim() })
    if (labels[1]) buttonRows.push({ type: b2Type, text: labels[1], url: '' })
    if (labels[2]) buttonRows.push({ type: b3Type, text: labels[2], url: '' })

    return (
      <div className={css(styles.buttonsWrap)}>
        {buttonRows.map((btn, idx) => this.renderButtonRow(btn, idx))}
      </div>
    )
  }

  renderButtonRow = (button, index) => {
    if (!button) return null
    const bubbleMode = !!this.props.messageBubbleMode
    const type = upper(button.type)
    const label = safeStr(button.text).trim() || `Button ${index + 1}`
    const iconName = this.getButtonIconName(type)
    const href = this.getButtonHref(button, type)
    const listOpensSheet = type === 'LIST' && !bubbleMode
    const hasAction = Boolean(href) || type === 'QUICK_REPLY' || type === 'FLOW' || listOpensSheet

    return (
      <button
        key={`btn_${index}`}
        type='button'
        className={css(styles.buttonRow)}
        onClick={() => this.handleButtonClick(href, type)}
        disabled={!hasAction}
      >
        {iconName && (
          <span className={css(styles.buttonIconWrap)} aria-hidden='true'>
            <span className={`material-icons-outlined ${css(styles.buttonIcon)}`}>
              {iconName}
            </span>
          </span>
        )}
        <span>{label}</span>
      </button>
    )
  }

  getButtonHref = (button, type) => {
    if (!button) return ''
    if (type === 'URL') {
      const raw = safeStr(button.url).trim()
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      return `https://${raw}`
    }
    if (type === 'PHONE' || type === 'PHONE_NUMBER') {
      const phone = safeStr(button.phone_number || button.phoneNumber).trim()
      if (!phone) return ''
      return `tel:${phone}`
    }
    return ''
  }

  handleButtonClick = (href, type) => {
    if (type === 'LIST') {
      if (this.props.messageBubbleMode) return
      const rows = getListRows(this.resolvedSession())
      if (rows.length === 0) return
      this.setState({ isListDialogOpen: true, selectedListRowId: '' })
      return
    }
    if (!href) return
    if (href.startsWith('tel:')) {
      window.location.href = href
      return
    }
    window.open(href, '_blank', 'noopener,noreferrer')
  }

  closeListDialog = () => {
    this.setState({ isListDialogOpen: false, selectedListRowId: '' })
  }

  onListRowSelect = (rowId) => {
    this.setState({ selectedListRowId: rowId })
  }

  onListSend = () => {
    if (!this.state.selectedListRowId) return
    this.closeListDialog()
  }

  renderListBottomSheet = () => {
    if (this.props.messageBubbleMode) return null
    const session = this.resolvedSession()
    const rows = getListRows(session)
    if (!this.state.isListDialogOpen || rows.length === 0) return null
    const sectionTitle = safeStr(session.sectionTitle).trim() || 'Options'
    const sendEnabled = Boolean(this.state.selectedListRowId)
    const sheet = (
      <div className={css(styles.listSheetOverlay)}>
        <button type='button' className={css(styles.listSheetBackdrop)} onClick={this.closeListDialog} />
        <div className={css(styles.listSheet)}>
          <div className={css(styles.listSheetHandle)} />
          <div className={css(styles.listSheetHeader)}>
            <button type='button' className={css(styles.listSheetClose)} onClick={this.closeListDialog}>
              <span className='material-icons-outlined'>close</span>
            </button>
            <div className={css(styles.listSheetTitle)}>{sectionTitle}</div>
            <div className={css(styles.listSheetHeaderSpacer)} />
          </div>
          <div className={css(styles.listSheetBody)}>
            {rows.map((row, idx) => {
              const selected = this.state.selectedListRowId === row.id
              return (
                <button
                  key={`row_${idx}`}
                  type='button'
                  className={css(styles.listOptionRow)}
                  onClick={() => this.onListRowSelect(row.id)}
                >
                  <div className={css(styles.listOptionText)}>
                    <div className={css(styles.listOptionTitle)}>{row.title}</div>
                    {row.description ? <div className={css(styles.listOptionDesc)}>{row.description}</div> : null}
                  </div>
                  <span className={css(styles.radioOuter, selected && styles.radioOuterSelected)}>
                    {selected && <span className={css(styles.radioInner)} />}
                  </span>
                </button>
              )
            })}
          </div>
          <div className={css(styles.listSheetFooter)}>
            {!sendEnabled && <div className={css(styles.listSheetHint)}>Tap to select an item</div>}
            {sendEnabled && (
              <button type='button' className={css(styles.listSendButton)} onClick={this.onListSend}>
                Send
              </button>
            )}
          </div>
        </div>
      </div>
    )
    const container = this.props.overlayContainerEl
    if (container) {
      return ReactDOM.createPortal(sheet, container)
    }
    return sheet
  }

  renderMessageInner = () => {
    const bubbleMode = !!this.props.messageBubbleMode
    return (
      <div className={css(bubbleMode ? styles.bubbleModeBubble : styles.bubble)}>
        {this.renderHeader()}
        {this.renderBody()}
        <div className={css(styles.metaRow, bubbleMode && styles.metaRowBubbleMode)}>
          {this.renderFooter()}
          <div className={css(styles.metaRowTrailing, bubbleMode && styles.metaRowTrailingBubbleMode)}>
            <span className={css(styles.timeText, bubbleMode && styles.timeTextBubbleMode)}>
              {this.renderBubbleTimeLabel()}
            </span>
            {bubbleMode && this.props.statusSlot ? (
              <span className={css(styles.metaRowStatusSlot)}>{this.props.statusSlot}</span>
            ) : null}
          </div>
        </div>
        {this.renderButtons()}
      </div>
    )
  }

  getButtonIconName = (type) => {
    if (type === 'PHONE' || type === 'PHONE_NUMBER') return 'phone'
    if (type === 'URL') return 'open_in_new'
    if (type === 'QUICK_REPLY') return 'reply'
    if (type === 'FLOW') return 'assignment'
    if (type === 'LIST') return 'list_alt'
    return ''
  }

  render() {
    if (this.props.messageBubbleMode) {
      return (
        <div className={css(styles.bubbleModeRoot)}>
          {this.renderMessageInner()}
        </div>
      )
    }
    return (
      <div className={css(styles.phoneShell)}>
        <div className={css(styles.chatArea)}>
          {this.renderMessageInner()}
        </div>
        {this.renderListBottomSheet()}
      </div>
    )
  }
}

const styles = StyleSheet.create({
  bubbleModeRoot: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
  },
  bubbleModeBubble: {
    marginLeft: 0,
    maxWidth: '100%',
    minWidth: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    boxShadow: 'none',
    color: '#111b21',
    overflow: 'hidden',
  },
  metaRowBubbleMode: {
    padding: '6px 0 2px',
  },
  metaRowTrailing: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    flex: '0 0 auto',
    marginLeft: 'auto',
  },
  metaRowTrailingBubbleMode: {
    gap: 5,
  },
  metaRowStatusSlot: {
    display: 'inline-flex',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  timeTextBubbleMode: {
    marginLeft: 0,
  },
  headerTextBubbleMode: {
    padding: 0,
  },
  bodyTextBubbleMode: {
    padding: 0,
  },
  documentCardBubbleMode: {
    margin: 0,
  },
  headerImageWrapBubbleMode: {
    width: 220,
    maxWidth: '100%',
    maxHeight: 'none',
    aspectRatio: '16 / 9',
    borderRadius: 6,
  },
  headerImageWrapLoadingBubbleMode: {
    minHeight: 0,
  },
  headerImageBubbleMode: {
    width: '100%',
    height: 'auto',
    maxHeight: '50vh',
    objectFit: 'contain',
  },
  headerVideoWrapBubbleMode: {
    width: 220,
    maxWidth: '100%',
  },
  headerVideoBubbleMode: {
    width: '100%',
    maxHeight: '50vh',
    display: 'block',
  },
  phoneShell: {
    position: 'relative',
    width: 340,
    maxWidth: '100%',
    margin: '0 auto',
    borderRadius: 24,
    backgroundColor: '#e5ddd5',
    border: '1px solid #d1d7db',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
  chatArea: {
    padding: 12,
    minHeight: 320,
    backgroundImage: 'radial-gradient(#ffffff66 1px, transparent 1px)',
    backgroundSize: '8px 8px',
  },
  bubble: {
    marginLeft: 'auto',
    maxWidth: '92%',
    minWidth: '72%',
    backgroundColor: '#e7ffdb',
    borderRadius: 8,
    boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
    color: '#111b21',
    overflow: 'hidden',
  },
  headerText: {
    padding: '12px 12px 0',
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '23px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  headerImageWrap: {
    width: '100%',
    maxHeight: 240,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#dfe4e8',
  },
  headerImageWrapLoading: {
    minHeight: 184,
  },
  headerImage: {
    width: '100%',
    display: 'block',
    maxHeight: 240,
    objectFit: 'cover',
  },
  headerImagePlaceholder: {
    position: 'absolute',
    inset: 0,
    backgroundImage: 'linear-gradient(100deg, #d7dde2 30%, #e8edf1 50%, #d7dde2 70%)',
    backgroundSize: '200% 100%',
    animationName: {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
    animationDuration: '1.2s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  headerImageHidden: {
    opacity: 0,
    transform: 'scale(1.01)',
  },
  headerImageVisible: {
    opacity: 1,
    transform: 'scale(1)',
    transition: 'opacity 220ms ease, transform 220ms ease',
  },
  headerVideo: {
    width: '100%',
    display: 'block',
    maxHeight: 240,
    backgroundColor: '#000',
  },
  documentCard: {
    margin: '8px 8px 2px',
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 0,
    borderRadius: 7,
    border: '1px solid #d9dee1',
    color: '#111b21',
    backgroundColor: '#f8f9fa',
    overflow: 'hidden',
  },
  documentTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px',
  },
  documentLeft: {
    flex: '0 0 auto',
  },
  documentFileTile: {
    width: 46,
    height: 56,
    borderRadius: 4,
    backgroundColor: '#d93025',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  documentFileIcon: {
    color: '#ffffff',
    fontSize: 16,
    lineHeight: 1,
    marginBottom: 2,
  },
  documentFileType: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    color: '#ffffff',
  },
  documentMeta: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  documentName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#111b21',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  documentSub: {
    fontSize: 12,
    color: '#667781',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  documentDownload: {
    flex: '0 0 auto',
    width: 32,
    height: 32,
    borderRadius: 16,
    border: '1px solid #e1e5e8',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 2,
  },
  documentDownloadIcon: {
    fontSize: 18,
    color: '#5f6f7a',
    lineHeight: 1,
  },
  missingMedia: {
    padding: 16,
    textAlign: 'center',
    color: '#54656f',
    fontSize: 12,
    borderBottom: '1px solid #d9efce',
    backgroundColor: '#f5f6f6',
  },
  bodyText: {
    padding: '12px 12px 0',
    fontSize: 15,
    lineHeight: '22px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  readMoreButton: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    marginLeft: 2,
    display: 'inline',
    verticalAlign: 'baseline',
    color: '#027eb5',
    fontSize: 13.5,
    lineHeight: '20px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  footerText: {
    color: '#667781',
    fontSize: 12.5,
    lineHeight: '16px',
    fontWeight: 500,
    wordBreak: 'break-word',
    flex: '1 1 auto',
    minWidth: 0,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    padding: '10px 12px 4px',
  },
  timeText: {
    textAlign: 'right',
    fontSize: 11,
    color: '#667781',
    lineHeight: '14px',
    flex: '0 0 auto',
    marginLeft: 'auto',
  },
  buttonsWrap: {
    marginTop: 8,
    borderTop: '1px solid #d9efce',
  },
  buttonRow: {
    width: '100%',
    border: 'none',
    borderTop: '1px solid #d9efce',
    backgroundColor: 'transparent',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 14,
    lineHeight: '20px',
    color: '#027eb5',
    fontWeight: 700,
    cursor: 'pointer',
  },
  buttonIconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonIcon: {
    fontSize: 16,
    lineHeight: 1,
    color: 'inherit',
  },
  listSheetOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 30,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'stretch',
  },
  listSheetBackdrop: {
    position: 'absolute',
    inset: 0,
    border: 'none',
    backgroundColor: 'rgba(17, 27, 33, 0.24)',
    cursor: 'pointer',
  },
  listSheet: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxHeight: '78%',
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    animationName: {
      '0%': { transform: 'translateY(100%)' },
      '100%': { transform: 'translateY(0%)' },
    },
    animationDuration: '220ms',
    animationTimingFunction: 'ease-out',
  },
  listSheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: '#D5DADF',
    margin: '8px auto 6px',
  },
  listSheetHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '4px 12px 10px',
    borderBottom: '1px solid #EEF1F4',
  },
  listSheetTitle: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: 15,
    fontWeight: 700,
    color: '#111b21',
    textAlign: 'center',
    maxWidth: '70%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listSheetClose: {
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#667781',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  listSheetHeaderSpacer: {
    width: 28,
    height: 28,
    marginLeft: 'auto',
    flex: '0 0 auto',
  },
  listSheetBody: {
    flex: '1 1 auto',
    overflowY: 'auto',
  },
  listOptionRow: {
    width: '100%',
    border: 'none',
    borderBottom: '1px solid #F0F2F5',
    backgroundColor: '#fff',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    textAlign: 'left',
    cursor: 'pointer',
  },
  listOptionText: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  listOptionTitle: {
    fontSize: 14,
    // fontWeight: 600,
    color: '#111b21',
  },
  listOptionDesc: {
    marginTop: 2,
    fontSize: 12,
    color: '#667781',
  },
  radioOuter: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2px solid #A0A7AF',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
  },
  radioOuterSelected: {
    borderColor: '#1DAA61',
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#1DAA61',
  },
  listSheetFooter: {
    padding: '5px 12px',
    borderTop: '1px solid #EEF1F4',
    minHeight: 45,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  listSheetHint: {
    fontSize: 13,
    color: '#667781',
    fontWeight: 500,
  },
  listSendButton: {
    width: '100%',
    border: 'none',
    borderRadius: 8,
    height: 40,
    fontSize: 14,
    color: '#fff',
    fontWeight: 600,
    backgroundColor: '#1DAA61',
    cursor: 'pointer',
  },
  strike: {
    textDecoration: 'line-through',
  },
  code: {
    backgroundColor: '#d8efd0',
    borderRadius: 3,
    padding: '0 4px',
    fontSize: 12.5,
    fontFamily: 'monospace',
  },
  bulletLine: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: 6,
  },
  bulletGlyph: {
    fontSize: '1em',
    lineHeight: '1.2em',
    marginTop: 1,
  },
  bulletText: {
    display: 'inline',
  },
  lineWrap: {
    display: 'inline',
  },
})

export default WhatsappSessionPreview
